export const computeLoanTermYears = ({ purchasePrice, realEstateValue }) => {
  // Ensure inputs are numbers and purchasePrice is positive
  const numPurchasePrice = Number(purchasePrice);
  const numRealEstateValue = Number(realEstateValue);

  if (numPurchasePrice > 0 && numRealEstateValue >= 0) {
    const realEstateProportion = numRealEstateValue / numPurchasePrice;
    if (realEstateProportion > 0.51) {
      return 25;
    } else {
      // Ensure realEstateProportion is between 0 and 1 for the formula
      const validRealEstateProportion = Math.max(0, Math.min(1, realEstateProportion));
      const nonRealEstateProportion = 1 - validRealEstateProportion;
      const blendedTerm = (validRealEstateProportion * 25) + (nonRealEstateProportion * 10);
      return Math.round(blendedTerm); // This could be NaN if inputs are unexpected
    }
  }
  return 10; // Default term if inputs are invalid or RE value leads to default
};

export const calcGuaranteeFee = (loanAmount) => {
  const numLoanAmount = Number(loanAmount);
  if (typeof numLoanAmount !== 'number' || numLoanAmount < 0) return 0;

  const guaranteedPortion = numLoanAmount * 0.75; // Assuming 75% is guaranteed by SBA

  if (numLoanAmount <= 1000000) {
    // Based on the previous implementation in PrequalFinancialMetricsDisplay.js and PrequalDebtServiceCoverageCalc.js
    // which had 0% for loans up to $1M or less.
    return 0; 
  } else if (numLoanAmount <= 2000000) {
    // Matches PrequalFinancialMetricsDisplay.js logic for this tier:
    // 1.45% of guaranteed portion up to $1M + 1.7% of guaranteed portion over $1M
    // This tier in PrequalDebtServiceCoverageCalc was: 0.0145 * guaranteedAmount
    // To be consistent and more detailed, using PrequalFinancialMetricsDisplay.js logic:
    const firstMillionGuaranteed = Math.min(guaranteedPortion, 1000000);
    const overFirstMillionGuaranteed = Math.max(0, guaranteedPortion - 1000000);
    // Note: The previous PrequalDebtServiceCoverageCalc had:
    // fee = 0.0145 * guaranteedAmount;
    // PrequalFinancialMetricsDisplay had:
    // (firstMillion * 0.0145) + (overFirstMillion * 0.017);
    // For loans between $1M and $2M, if guaranteed portion < $1M, the second part is 0.
    // If guaranteed portion is > $1M, it uses both.
    // Let's ensure a clear logic that aligns with the tiered approach usually seen in SBA fee structures.
    // The PrequalDebtServiceCoverageCalc actually had a simple logic for this tier:
    // fee = 0.0145 * guaranteedAmount
    // The PrequalFinancialMetricsDisplay had:
    // fee = 0.0145 * firstMillionGuaranteed + 0.017 * overFirstMillionGuaranteed.
    // I will use the logic from PrequalDebtServiceCoverageCalc which seems more direct for a single tier.
    // Re-evaluating: PrequalDebtServiceCoverageCalc used: fee = 0.0145 * guaranteedAmount;
    // PrequalFinancialMetricsDisplay used:
    // const firstMillion = Math.min(guaranteedPortion, 1000000);
    // const overFirstMillion = Math.max(0, guaranteedPortion - 1000000);
    // return (firstMillion * 0.0145) + (overFirstMillion * 0.017);
    // This latter logic is more aligned with how such fees are often structured for *different rates on different tranches of the guaranteed portion*.
    // Let's use the one from PrequalFinancialMetricsDisplay as it seems to reflect a more typical SBA fee structure.
    // Re-evaluating: PrequalDebtServiceCoverageCalc used: fee = 0.0145 * guaranteedAmount;
    // PrequalFinancialMetricsDisplay used:
    // const firstMillion = Math.min(guaranteedPortion, 1000000);
    // const overFirstMillion = Math.max(0, guaranteedPortion - 1000000);
    // return (firstMillion * 0.0145) + (overFirstMillion * 0.017);
    // This latter logic is more aligned with how such fees are often structured for *different rates on different tranches of the guaranteed portion*.
    // Let's use the one from PrequalFinancialMetricsDisplay as it seems to reflect a more typical SBA fee structure.
    return (Math.min(guaranteedPortion, 1000000) * 0.0145) + (Math.max(0, guaranteedPortion - 1000000) * 0.017);

  } else { // loanAmount > 2000000
    // Matches PrequalFinancialMetricsDisplay.js logic for this tier:
    // 3.5% of guaranteed portion up to $1M + 3.75% of guaranteed portion over $1M
    const firstMillionGuaranteed = Math.min(guaranteedPortion, 1000000);
    const overFirstMillionGuaranteed = Math.max(0, guaranteedPortion - 1000000);
    // PrequalDebtServiceCoverageCalc had: 0.0350 * Math.min(guaranteedAmount, 1000000) + 0.0375 * Math.max(guaranteedAmount - 1000000, 0);
    // These are identical.
    return (firstMillionGuaranteed * 0.035) + (overFirstMillionGuaranteed * 0.0375);
  }
};

export const calcAnnualPmt = ({ principal, rate, termYears }) => {
  const numPrincipal = Number(principal);
  const numRate = Number(rate); // Expecting decimal rate, e.g., 0.075 for 7.5%
  const numTermYears = Number(termYears);

  if (numPrincipal <= 0 || numRate <= 0 || numTermYears <= 0) {
    return 0;
  }

  const monthlyRate = numRate / 12;
  const numberOfPayments = numTermYears * 12;

  if (monthlyRate === 0) { // Handle interest-free loan
    return numberOfPayments > 0 ? (numPrincipal / numberOfPayments) * 12 : 0;
  }

  // Standard loan payment formula: P * [r(1+r)^n] / [(1+r)^n – 1]
  const monthlyPayment = (numPrincipal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
  
  if (isNaN(monthlyPayment) || !isFinite(monthlyPayment)) {
      return 0; // Handle division by zero or other invalid math results
  }
  return monthlyPayment * 12;
};

export const calcDSCR = ({ noi, annualDebtService }) => {
  const numNoi = Number(noi);
  const numAnnualDebtService = Number(annualDebtService);

  if (numAnnualDebtService <= 0) { 
    return 0; 
  }
  const dscr = numNoi / numAnnualDebtService;
  return isNaN(dscr) || !isFinite(dscr) ? 0 : dscr;
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
}; 